import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Link2, Users } from "lucide-react";
import api, { thumb } from "../lib/api";

const prettyDate = (s) => {
  const [y, m, d] = (s || "").split("-").map(Number);
  return y ? new Date(y, m - 1, d, 12).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) : "Date to be confirmed";
};

export default function RsvpTracker() {
  const { token } = useParams();
  const [t, setT] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    const load = () => api.get(`/private-events/tracker/${token}`).then(({ data }) => setT(data)).catch(() => setMissing(true));
    load();
    const iv = setInterval(load, 60000);
    return () => { clearInterval(iv); m.remove(); };
  }, [token]);

  if (missing) return <div data-testid="rsvp-missing" className="pt-40 px-6 text-center text-stone-600">This RSVP tracker link isn't valid.</div>;
  if (!t) return <div className="pt-32 px-6 text-stone-500">Loading…</div>;

  const pct = t.capacity ? Math.round((t.booked / t.capacity) * 100) : 0;
  const link = `${window.location.origin}/classes/${t.slug}`;
  const copy = () => { navigator.clipboard?.writeText(link); toast.success("Booking link copied — share it with your guests!"); };

  return (
    <div data-testid="rsvp-page" className="pt-32 pb-24 fade-in">
      <div className="max-w-3xl mx-auto px-6 sm:px-12">
        <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#E0218A]">RSVP tracker · Private event</p>
        <h1 data-testid="rsvp-title" className="font-serif font-black text-4xl sm:text-5xl text-[#121212] mt-3 leading-[1.05]">{t.title}</h1>
        <p className="mt-3 text-stone-600">{prettyDate(t.date)}{t.time ? ` · ${t.time}` : ""}{t.location ? ` · ${t.location}` : ""}</p>
        {t.image_url && <img src={thumb(t.image_url, 900)} alt="" className="mt-8 w-full aspect-[16/7] object-cover border-2 border-[#121212]" />}

        <div className="mt-8 border-2 border-[#121212] bg-[#F5EFE6] p-6 sm:p-8">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500">Spots booked</p>
              <p data-testid="rsvp-booked" className="font-serif font-black text-6xl text-[#121212] mt-1">{t.booked}<span className="text-2xl text-stone-400"> / {t.capacity}</span></p>
            </div>
            <div className="text-right">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500">Spots left</p>
              <p data-testid="rsvp-left" className="font-serif font-black text-6xl text-[#E0218A] mt-1">{t.left}</p>
            </div>
          </div>
          <div className="mt-6 h-4 bg-white border-2 border-[#121212]">
            <div data-testid="rsvp-progress" className="h-full bg-[#C6FF33] transition-[width] duration-700" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-2 text-xs text-stone-500 inline-flex items-center gap-1"><Users size={12} /> {pct}% full{t.left === 0 ? " — your event is fully booked!" : ""}</p>
        </div>

        <div data-testid="rsvp-attendees" className="mt-6 border-2 border-[#121212] p-6">
          <p className="font-serif font-black text-xl text-[#121212]">Who's coming ({(t.attendees || []).length})</p>
          {(t.attendees || []).length ? (
            <ol className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-[#121212] list-decimal list-inside">
              {t.attendees.map((n, i) => <li key={i} data-testid={`rsvp-attendee-${i}`}>{n}</li>)}
            </ol>
          ) : <p className="mt-2 text-sm text-stone-500">No one has booked yet — share the link below!</p>}
        </div>

        {t.rsvp_by && (
          <p data-testid="rsvp-deadline" className={`mt-4 text-sm font-bold px-4 py-3 border-2 border-[#121212] ${t.rsvp_closed ? "bg-stone-200 text-stone-700" : "bg-[#C6FF33] text-[#121212]"}`}>
            {t.rsvp_closed ? "RSVPs are closed — the booking link no longer accepts new guests." : `RSVP by ${prettyDate(t.rsvp_by)} — the booking link closes at 11:59 PM that night.`}
          </p>
        )}
        {!t.active && <p data-testid="rsvp-not-open" className="mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 px-4 py-3">Booking isn't open yet — Cake Society will let you know when your link is ready to share.</p>}

        <div className="mt-8 border-2 border-[#121212] p-6">
          <p className="font-serif font-black text-xl text-[#121212]">Share with your guests</p>
          <p className="text-sm text-stone-600 mt-1">Each guest books and pays for their own spot with this link.</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <code data-testid="rsvp-booking-link" className="flex-1 text-xs bg-stone-50 border border-stone-200 px-3 py-3 truncate">{link}</code>
            <button data-testid="rsvp-copy-link" onClick={copy} className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#121212] text-[#C6FF33] text-xs tracking-[0.2em] uppercase font-bold hover:bg-black transition-colors"><Link2 size={14} /> Copy link</button>
          </div>
        </div>
        <p className="mt-4 text-xs text-stone-400">This page updates automatically.</p>
      </div>
    </div>
  );
}
